import { parseIncomeYen, formatIncomeYen } from '../spouseSection/spouseEligibility';
import type {
  SingleParentCandidate,
  SpecialStatusOutcome,
  SpecialStatusSnapshot,
  WidowSpouseLost,
  YesNo,
} from './specialStatusTypes';

export const SINGLE_PARENT_INCOME_LIMIT = 5_000_000;
export const WIDOW_INCOME_LIMIT = 5_000_000;
export const WORKING_STUDENT_INCOME_LIMIT = 750_000;
export const WORKING_STUDENT_SALARY_REVENUE_LIMIT = 1_300_000;
export const WORKING_STUDENT_NON_EMPLOYMENT_LIMIT = 100_000;

export function isSingleParentIncomeIneligible(employeeIncome: number): boolean {
  return employeeIncome > SINGLE_PARENT_INCOME_LIMIT;
}

export function usesSingleParentChildLoop(candidateCount: number): boolean {
  return candidateCount > 1;
}

export function evaluateSingleParent(
  maritalStatus: 'married' | 'not_married' | '',
  snap: SpecialStatusSnapshot,
  employeeIncome: number
): SpecialStatusOutcome {
  if (maritalStatus === 'married') return 'skipped';
  if (snap.singleParentChildConfirmed !== 'yes') return 'ineligible';
  if (snap.singleParentClaimedByOther === 'yes') return 'ineligible';
  if (isSingleParentIncomeIneligible(employeeIncome)) return 'ineligible';
  return 'eligible';
}

/** After a child fails, try the next one only when income is not the blocker. */
export function shouldTryNextSingleParentChild(opts: {
  childIndex: number;
  candidateCount: number;
  employeeIncome: number;
  confirmed: YesNo | '';
  claimedByOther: YesNo | '';
}): boolean {
  if (!usesSingleParentChildLoop(opts.candidateCount)) return false;
  if (opts.childIndex >= opts.candidateCount - 1) return false;
  if (isSingleParentIncomeIneligible(opts.employeeIncome)) return false;
  if (opts.confirmed === 'no') return true;
  if (opts.confirmed === 'yes' && opts.claimedByOther === 'yes') return true;
  return false;
}

export function evaluateWidow(
  maritalStatus: 'married' | 'not_married' | '',
  snap: SpecialStatusSnapshot,
  employeeIncome: number
): SpecialStatusOutcome {
  if (maritalStatus === 'married') return 'skipped';
  if (snap.widowSpouseLost !== 'death' && snap.widowSpouseLost !== 'divorce') return 'ineligible';
  if (snap.widowCurrentlyRemarried === 'yes') return 'ineligible';
  if (employeeIncome > WIDOW_INCOME_LIMIT) return 'ineligible';
  return 'eligible';
}

export function evaluateWorkingStudent(
  snap: SpecialStatusSnapshot,
  employeeIncome: number
): SpecialStatusOutcome {
  if (snap.workingStudentEnrolled !== 'yes') return 'ineligible';
  if (!snap.workingStudentSchoolType) return 'ineligible';

  const nonEmp = parseIncomeYen(snap.workingStudentNonEmploymentIncome);
  if (snap.workingStudentHasNonEmploymentIncome === 'yes' && nonEmp > WORKING_STUDENT_NON_EMPLOYMENT_LIMIT) {
    return 'ineligible';
  }
  if (snap.workingStudentHasNonEmploymentIncome === 'no') {
    // no non-employment income — ok
  } else if (snap.workingStudentHasNonEmploymentIncome !== 'yes') {
    return 'ineligible';
  }

  if (snap.workingStudentSalaryOnly === 'yes') {
    const salaryRev = parseIncomeYen(snap.workingStudentSalaryRevenue);
    if (salaryRev <= 0 || salaryRev > WORKING_STUDENT_SALARY_REVENUE_LIMIT) return 'ineligible';
    return 'eligible';
  }

  if (snap.workingStudentSalaryOnly === 'no') {
    if (employeeIncome <= 0 || employeeIncome > WORKING_STUDENT_INCOME_LIMIT) return 'ineligible';
    return 'eligible';
  }

  return 'ineligible';
}

export function inferWidowLossFromSpouseChangeReason(reason: string | null | undefined): WidowSpouseLost {
  const r = (reason ?? '').trim().toLowerCase();
  if (r === 'death') return 'death';
  if (r === 'divorce') return 'divorce';
  return '';
}

export function formatIncomeDisplay(raw: string): string {
  const n = parseIncomeYen(raw);
  return n > 0 ? `${formatIncomeYen(n)} yen` : '—';
}

export function singleParentResultMessage(
  outcome: SpecialStatusOutcome,
  employeeIncome: number,
  t: (k: string) => string
): string {
  if (outcome === 'skipped') return t('submitDocuments.dependentDeduction.specialStatus.singleParent.result.skipped');
  if (outcome === 'eligible') {
    return t('submitDocuments.dependentDeduction.specialStatus.singleParent.result.eligible');
  }
  if (employeeIncome > SINGLE_PARENT_INCOME_LIMIT) {
    return t('submitDocuments.dependentDeduction.specialStatus.singleParent.result.incomeOver');
  }
  return t('submitDocuments.dependentDeduction.specialStatus.singleParent.result.ineligible');
}

export function widowResultMessage(
  outcome: SpecialStatusOutcome,
  employeeIncome: number,
  t: (k: string) => string
): string {
  if (outcome === 'skipped') return t('submitDocuments.dependentDeduction.specialStatus.widow.result.skipped');
  if (outcome === 'eligible') return t('submitDocuments.dependentDeduction.specialStatus.widow.result.eligible');
  if (employeeIncome > WIDOW_INCOME_LIMIT) {
    return t('submitDocuments.dependentDeduction.specialStatus.widow.result.incomeOver');
  }
  return t('submitDocuments.dependentDeduction.specialStatus.widow.result.ineligible');
}

export function workingStudentResultMessage(outcome: SpecialStatusOutcome, t: (k: string) => string): string {
  if (outcome === 'eligible') return t('submitDocuments.dependentDeduction.specialStatus.workingStudent.result.eligible');
  return t('submitDocuments.dependentDeduction.specialStatus.workingStudent.result.ineligible');
}

export function pickDefaultCandidate(candidates: SingleParentCandidate[]): SingleParentCandidate | null {
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0];
  return null;
}

export function ynToBool(v: YesNo | ''): boolean | undefined {
  if (v === 'yes') return true;
  if (v === 'no') return false;
  return undefined;
}

export function boolToYn(v: boolean | null | undefined): YesNo | '' {
  if (v === true) return 'yes';
  if (v === false) return 'no';
  return '';
}
